import { useEffect, useState } from "react"
import type { CSSProperties } from "react"

import frameUrl from "@/assets/textures/header-frame.svg?url"
import maskUrl from "@/assets/textures/header-frame-mask.svg?url"
import bannerBgUrl from "@/assets/textures/tournament-top-header.png"
import TonIcon from "@/assets/icons/ton-icon.svg?react"
import { useI18n } from "@/shared/i18n"
import { formatTonFromNano } from "@/shared/lib/pvpRouletteMath"
import { cn } from "@/shared/lib/utils"
import type { TournamentSummary } from "@/shared/api/types/tournaments"

/*
 * Pointed-hexagon "reel" frame.
 *
 * Both the gold stroke and the texture fill use the SAME nine-slice geometry —
 * the gold via `border-image` of `header-frame.svg`, the fill via
 * `-webkit-mask-box-image` of `header-frame-mask.svg` (a solid silhouette of
 * the same shape). Identical `slice` (`0 19 fill`) + `width` (`0 15px`) means:
 *   - the end caps (the converging points) are FIXED size → never distort;
 *   - only the flat top/bottom rails stretch horizontally;
 *   - stroke and fill share one slice geometry → they stay perfectly aligned.
 * (`-webkit-mask-box-image` is supported in the WebKit/Blink Telegram webviews.)
 *
 * Tuning knobs: `SLICE` (source px treated as the fixed end cap) and the `15px`
 * cap render width in both layers — keep them in sync.
 */
const SLICE = "0 19 fill"
const CAP_WIDTH = "0 15px"

/**
 * Both layers use the SAME slice/cap-width via shorthand props so the mask
 * geometry (fill) and the border-image geometry (gold frame) are guaranteed to
 * line up at every banner width — no chance of a longhand falling back to a
 * different default (e.g. mask-box-image-width inheriting border-width=0).
 */
const FILL_STYLE: CSSProperties = {
  backgroundImage: `linear-gradient(90deg, rgba(0,0,0,0.72) 40%, rgba(0,0,0,0.1) 100%), url(${bannerBgUrl})`,
  backgroundSize: "cover",
  // Pin the cat (right side of tournament-top-header.png) to the banner's
  // right edge — the left gets cropped if the banner is narrower than aspect.
  backgroundPosition: "right center",
  // NB: data-URI may contain literal `'` (Vite emits unquoted SVG attrs); CSS
  // spec disallows quotes inside unquoted url(...), so wrap in "..." to keep
  // strict parsers (some webviews) from rejecting the value.
  WebkitMaskBoxImage: `url("${maskUrl}") ${SLICE} / ${CAP_WIDTH} stretch`,
}

const FRAME_STYLE: CSSProperties = {
  borderStyle: "solid",
  borderColor: "transparent",
  borderWidth: "0 15px",
  borderImage: `url("${frameUrl}") ${SLICE} / ${CAP_WIDTH} stretch`,
  filter: "drop-shadow(0 0 4px rgba(252, 210, 85, 0.55))",
}

interface TournamentBannerProps {
  tournament: TournamentSummary
  onClick?: () => void
  className?: string
}

/** Live `HH:MM:SS` until `targetMs` (clamped at zero), updating once per second. */
function useCountdownText(targetMs: number): string {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  const remainingSec = Math.max(0, Math.floor((targetMs - now) / 1000))
  const h = Math.floor(remainingSec / 3600)
  const m = Math.floor((remainingSec % 3600) / 60)
  const s = remainingSec % 60
  const pad = (n: number) => n.toString().padStart(2, "0")
  return `${pad(h)}:${pad(m)}:${pad(s)}`
}

/**
 * Tournament banner in the PvP top row (between the history button and the
 * balance pill): texture-backed pointed-hexagon plaque with the gold notched
 * frame. Two-line content per Figma — top: pool + "Prize pool"; bottom:
 * countdown ("Starts in" / "Ends in" / "Ended"). Tournament name is intentionally
 * not shown here.
 *
 * Presentational — takes a `TournamentSummary` directly so it can be previewed
 * in the emulator; InfoBlock supplies it from `useTournamentStore`.
 */
export function TournamentBanner({
  tournament,
  onClick,
  className,
}: TournamentBannerProps) {
  const { t } = useI18n()
  const pool = formatTonFromNano(tournament.currentPoolNanoTon, {
    allowZero: true,
  })

  const now = Date.now()
  const isUpcoming = tournament.isUpcoming || now < tournament.startsAt
  const isEnded =
    tournament.isFinished || (now >= tournament.endsAt && tournament.endsAt > 0)
  const countdownTarget = isUpcoming ? tournament.startsAt : tournament.endsAt
  const countdownText = useCountdownText(countdownTarget)
  const countdownLabel = isEnded
    ? t("tournamentBanner.ended")
    : isUpcoming
      ? t("tournamentBanner.startsIn")
      : t("tournamentBanner.endsIn")

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={tournament.name}
      className={cn("relative h-14 min-w-0 flex-1 shrink", className)}
    >
      {/* Texture fill, masked to the hexagon (nine-slice) */}
      <span aria-hidden className="absolute inset-0" style={FILL_STYLE} />
      {/* Gold notched frame (nine-slice) */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={FRAME_STYLE}
      />

      {/* Content overlay */}
      <span className="absolute inset-0 flex items-center gap-2.5 pl-5 pr-3">
        {/* Carousel-style dots */}
        <span aria-hidden className="flex shrink-0 flex-col gap-0.5">
          <span className="size-1 rounded-full bg-white" />
          <span className="size-1 rounded-full bg-white/30" />
          <span className="size-1 rounded-full bg-white/30" />
        </span>

        <span className="flex min-w-0 flex-1 flex-col items-start justify-center text-left">
          {/* Top: {pool} [TON] Prize pool, semibold 12 */}
          <span className="flex items-center gap-1 text-xs font-semibold leading-4 tracking-[-0.5px] text-white">
            <span className="flex items-center gap-0.5">
              {pool}
              <TonIcon className="size-3 shrink-0 -translate-y-px" />
            </span>
            <span>{t("tournamentBanner.prizePool")}</span>
          </span>
          {/* Bottom: countdown label + HH:MM:SS, regular 12 */}
          <span className="flex items-center gap-1 text-xs leading-4 tracking-[-0.5px] text-white">
            <span>{countdownLabel}</span>
            {!isEnded && <span className="tabular-nums">{countdownText}</span>}
          </span>
        </span>
      </span>
    </button>
  )
}
